
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import PageLayout from "@/components/layout/PageLayout";
import { useToast } from "@/hooks/use-toast";
import { AdminBreadcrumb } from "@/components/admin/AdminBreadcrumb";
import { EmployeeForm } from "@/components/admin/employees/EmployeeForm";
import { EmployeeHeader } from "@/components/admin/employees/EmployeeHeader";
import { Card } from "@/components/ui/card";
import { Employee } from "@/components/admin/employees/types";
import { AppRole } from "@/types/database/auth";
import { Spinner } from "@/components/ui/spinner";

const EmployeeDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(true);
  const isNewEmployee = id === 'new';

  useEffect(() => {
    const fetchEmployee = async () => {
      try {
        if (isNewEmployee) {
          setLoading(false);
          return;
        }

        // Get current user's session
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id) {
          throw new Error("No authenticated session");
        }

        // Verify the user is strictly an admin (not producer)
        const { data: userRole, error: roleError } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", session.user.id)
          .eq("role", "admin")
          .single();
        
        if (roleError || !userRole) {
          console.error("Admin check error:", roleError);
          throw new Error("Unauthorized - admin access required");
        }

        // Fetch employee profile
        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", id)
          .single();

        if (profileError) {
          console.error("Profile fetch error:", profileError);
          throw profileError;
        }

        // Fetch employee role
        const { data: roleData, error: roleError2 } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", id)
          .maybeSingle();

        if (roleError2) {
          console.error("Role fetch error:", roleError2);
          throw roleError2;
        }

        if (!profileData) {
          toast({
            variant: "destructive",
            title: "Not Found",
            description: "The requested employee could not be found.",
          });
          navigate("/admin/employees");
          return;
        }

        // Type check the role before setting it
        const role = roleData?.role as AppRole | undefined;
        
        // Combine profile data with role
        setEmployee({
          ...profileData,
          role: role || "user"
        });
      } catch (error: any) {
        console.error("Error fetching employee:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: error.message.includes("Unauthorized") 
            ? "Only administrators can manage employees" 
            : "Failed to fetch employee details.",
        });
        navigate("/admin");
      } finally {
        setLoading(false);
      }
    };

    fetchEmployee();
  }, [id, isNewEmployee, toast, navigate]);

  useEffect(() => {
    // Separate check for admin role on initial load
    const checkAdminAccess = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate("/auth");
          return;
        }

        // Check if user is admin (not producer)
        const { data, error } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", session.user.id)
          .eq("role", "admin")
          .single();

        if (error || !data) {
          toast({
            variant: "destructive",
            title: "Access Denied",
            description: "Only administrators can manage employees.",
          });
          navigate("/admin");
        }
      } catch (error) {
        console.error("Error checking admin access:", error);
        navigate("/admin");
      }
    };

    checkAdminAccess();
  }, [navigate, toast]);

  if (loading) {
    return (
      <PageLayout>
        <div className="container mx-auto px-4 py-6">
          <Card className="p-6">
            <div className="flex justify-center items-center p-6">
              <Spinner size="lg" />
            </div>
          </Card>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="container mx-auto px-4 py-6">
        <AdminBreadcrumb 
          currentPage={isNewEmployee ? 'Create Employee' : 'Edit Employee'} 
          parentPath="/admin/employees"
          parentLabel="Employees"
        />
        <div className="space-y-6">
          <EmployeeHeader isNewEmployee={isNewEmployee} />
          <EmployeeForm 
            employee={employee as Employee}
            isNewEmployee={isNewEmployee}
          />
        </div>
      </div>
    </PageLayout>
  );
};

export default EmployeeDetailsPage;
